/**
 * Exportul rapoartelor în Excel și PDF.
 *
 * Un raport se descrie ca o listă de TABELE — titlu, coloane, rânduri — iar
 * exportul lucrează pe descrierea aia, nu pe ecran. Motivul e practic: un
 * raport nou n-are de scris cod de export, iar cele două formate nu pot
 * ajunge să arate lucruri diferite, fiindcă citesc aceeași descriere.
 *
 * Bibliotecile se încarcă la CERERE, nu la pornirea aplicației: `jspdf`
 * înseamnă câteva sute de kilobytes pe care nu-i plătește nimeni până nu apasă
 * cineva „PDF". `xlsx` era deja în pachet, din importul de catalog.
 */

import { tr, locale } from "./i18n.js";

const azi = () => new Date().toISOString().slice(0, 10);

/** Numele fișierului: raportul, perioada, data. Nu „export(3).xlsx". */
function numeFisier(raport, filtru, ext) {
  const parti = [`MMT-${raport}`];
  if (filtru?.de || filtru?.pana) parti.push(`${filtru.de ?? "inceput"}_${filtru.pana ?? azi()}`);
  else parti.push(azi());
  return `${parti.join("-").replace(/[^\w.-]/g, "-")}.${ext}`;
}

const valoare = (rand, col) => {
  const v = typeof col.valoare === "function" ? col.valoare(rand) : rand[col.cheie];
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toLocaleDateString(locale());
  return v;
};

/**
 * @param {string} raport      numele scurt, pentru fișier
 * @param {object} filtru      perioada, pentru numele fișierului
 * @param {Array}  tabele      [{ titlu, coloane: [{cheie, eticheta, valoare?}], randuri }]
 */
export async function exportaExcel(raport, filtru, tabele) {
  const XLSX = await import("xlsx");
  const wb = XLSX.utils.book_new();
  for (const t of tabele) {
    if (!t.randuri?.length) continue;
    const antet = t.coloane.map((c) => tr(c.eticheta));
    const corp = t.randuri.map((r) => t.coloane.map((c) => valoare(r, c)));
    const ws = XLSX.utils.aoa_to_sheet([antet, ...corp]);
    // Numele de foaie din Excel are 31 de caractere și nu suportă []:*?/\
    const nume = String(tr(t.titlu)).replace(/[[\]:*?/\\]/g, " ").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, nume || tr("Raport"));
  }
  if (!wb.SheetNames.length) throw new Error(tr("Raportul nu are niciun rând de exportat."));
  XLSX.writeFile(wb, numeFisier(raport, filtru, "xlsx"));
}

export async function exportaPdf(raport, titlu, filtru, tabele) {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const autoTable = autoTableMod.default ?? autoTableMod.autoTable;

  // Peisaj: rapoartele au coloane multe, iar un tabel rupt pe două pagini în
  // lățime nu se mai citește deloc.
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  doc.setFontSize(14);
  doc.text(tr(titlu), 40, 40);
  doc.setFontSize(9);
  const perioada = filtru?.de || filtru?.pana
    ? tr("Perioada: {0} — {1}", filtru.de ?? tr("început"), filtru.pana ?? azi())
    : tr("Generat la {0}", new Date().toLocaleString(locale()));
  doc.text(perioada, 40, 56);

  let y = 76;
  let ceva = false;
  for (const t of tabele) {
    if (!t.randuri?.length) continue;
    ceva = true;
    // Titlul stă DEASUPRA tabelului, nu în prima celulă: un titlu pus în tabel
    // se sortează și se filtrează odată cu datele.
    doc.setFontSize(11);
    doc.text(String(tr(t.titlu)), 40, y);
    doc.setFontSize(9);
    autoTable(doc, {
      startY: y + 12,
      head: [t.coloane.map((c) => tr(c.eticheta))],
      body: t.randuri.map((r) => t.coloane.map((c) => String(valoare(r, c)))),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [11, 18, 16], textColor: 255 },
      margin: { left: 40, right: 40 },
    });
    const dupa = doc.lastAutoTable?.finalY ?? y;
    y = dupa + 28;
    if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = 60; }
  }
  if (!ceva) throw new Error(tr("Raportul nu are niciun rând de exportat."));
  doc.save(numeFisier(raport, filtru, "pdf"));
}
